/**
 * Форматирование денег и времени.
 *
 * Время всегда приводится к таймзоне семьи, а не устройства: няня в поездке
 * и родитель дома должны видеть одинаковое «18:34».
 */

const CURRENCY_SYMBOL: Record<string, string> = {
  RUB: '₽',
  USD: '$',
  EUR: '€',
  KZT: '₸',
  BYN: 'Br',
}

/** "4750.00" → "4 750 ₽". Копейки показываем, только если они есть. */
export function formatMoney(value: string | number | null | undefined, currency = 'RUB'): string {
  if (value === null || value === undefined || value === '') return '—'
  const amount = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(amount)) return '—'

  const symbol = CURRENCY_SYMBOL[currency] ?? currency
  const hasCents = Math.abs(amount % 1) > 0.001
  const formatted = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(amount)
  // неразрывный пробел: сумма и знак валюты не должны разъезжаться по строкам
  return `${formatted}\u00A0${symbol}`
}

/** Только число, без валюты — для крупных сумм с отдельным знаком. */
export function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const amount = typeof value === 'number' ? value : Number(value)
  if (Number.isNaN(amount)) return '—'
  const hasCents = Math.abs(amount % 1) > 0.001
  return new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: hasCents ? 2 : 0,
  }).format(amount)
}

export function currencySymbol(currency = 'RUB'): string {
  return CURRENCY_SYMBOL[currency] ?? currency
}

/** 570 → "9 ч 30 мин" */
export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes))
  const hours = Math.floor(total / 60)
  const mins = total % 60
  if (hours && mins) return `${hours} ч ${mins} мин`
  if (hours) return `${hours} ч`
  return `${mins} мин`
}

/** Короткая длительность для «идёт прогулка»: 0:42 */
export function formatClock(minutes: number): string {
  const total = Math.max(0, Math.round(minutes))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

export function formatTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso))
}

export function formatDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(new Date(iso))
}

export function formatWeekday(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'long', timeZone }).format(new Date(iso))
}

/** «вторник, 24 августа» — тёплая подпись под заголовком экрана. */
export function formatLongDay(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(date)
}

/** YYYY-MM-DD в таймзоне семьи — ключ дня для запросов. */
export function familyDate(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(date)
  return parts
}

/** «Сегодня» / «Вчера» / «5 сентября» — заголовки в ленте. */
export function formatDayLabel(dayIso: string, timeZone: string, now = new Date()): string {
  const today = familyDate(now, timeZone)
  const yesterday = familyDate(new Date(now.getTime() - 86_400_000), timeZone)
  if (dayIso === today) return 'Сегодня'
  if (dayIso === yesterday) return 'Вчера'
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    timeZone,
  }).format(new Date(`${dayIso}T12:00:00Z`))
}

/** «5 минут назад» — мягче абсолютного времени для свежих событий. */
export function formatRelative(iso: string, now = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime()
  const minutes = Math.round(diffMs / 60_000)
  if (minutes < 1) return 'только что'
  if (minutes < 60) return `${minutes} мин назад`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} ч назад`
  return `${Math.round(hours / 24)} дн назад`
}

export function formatPeriod(startIso: string, endIso: string, timeZone: string): string {
  const start = new Date(`${startIso}T12:00:00Z`)
  const end = new Date(`${endIso}T12:00:00Z`)
  const sameMonth = start.getUTCMonth() === end.getUTCMonth()
  const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', timeZone })
  const fullFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', timeZone })
  return sameMonth
    ? `${dayFmt.format(start)}–${fullFmt.format(end)}`
    : `${fullFmt.format(start)} – ${fullFmt.format(end)}`
}

/** «3 события» — склонение там, где число видно человеку. */
export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}
